import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import { Label } from "@/components/chadcn/Label"
import { SelectInput, TextareaInput, TextInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSelfQuery } from "@/data/queries"
import {
  useFormByIdQuery,
  useUpdateBloodGlucoseFormMutation,
} from "@/data/queries/measurementQueries"
import type { BloodGlucoseForm } from "@/model/BloodGlucoseForm"
import { ROUTES } from "@/routes"
import { foodLogSchema, type FoodLogFormData, type MealContext } from "@/schemas/measurements"
import { zodResolver } from "@hookform/resolvers/zod"
import { Controller, useForm } from "react-hook-form"
import { useLocation, useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import DateTimePicker from "./components/DateTimePicker"
import NotesField from "./components/NotesField"

const mealContextOptions: { value: MealContext; title: string }[] = [
  { value: "breakfast", title: "Breakfast" },
  { value: "lunch", title: "Lunch" },
  { value: "dinner", title: "Dinner" },
  { value: "snack-before-breakfast", title: "Snack before breakfast" },
  {
    value: "snack-between-breakfast-and-lunch",
    title: "Snack between breakfast and lunch",
  },
  {
    value: "snack-between-lunch-and-dinner",
    title: "Snack between lunch and dinner",
  },
  { value: "snack-after-dinner", title: "Snack after dinner" },
]

const isMealContext = (context: string): context is MealContext =>
  mealContextOptions.some((option) => option.value === context)

// Rendered only once form data is loaded
const FoodLogEditForm = ({
  form,
  patientId,
  returnUrl,
}: {
  form: BloodGlucoseForm
  patientId: string
  returnUrl: string
}) => {
  const navigate = useNavigate()
  const { mutateAsync: updateForm, isPending } = useUpdateBloodGlucoseFormMutation({
    patientId,
    formId: form.id,
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FoodLogFormData>({
    mode: "onSubmit",
    resolver: zodResolver(foodLogSchema),
    defaultValues: {
      context: isMealContext(form.context) ? form.context : "breakfast",
      dateTime: form.dateTime.toString(),
      mealContents: form.mealContents ?? "",
      carbsInGrams: form.carbsInGrams ?? undefined,
      proteinInGrams: form.proteinInGrams ?? undefined,
      notes: form.notes ?? undefined,
    },
  })

  const onSubmit = async (formData: FoodLogFormData) => {
    try {
      await updateForm({
        formId: form.id,
        update: {
          context: formData.context,
          mealContents: formData.mealContents,
          carbsInGrams: formData.carbsInGrams ?? null,
          proteinInGrams: formData.proteinInGrams ?? null,
          notes: formData.notes ?? null,
        },
      })
      navigate(returnUrl)
    } catch {
      toast.error("Failed to save food log. Please try again.")
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <BackButton customClickHandler={() => navigate(returnUrl)} />

      <h1 className="text-2xl font-bold text-primary-contrast">Edit meal log</h1>

      {!form.editable && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <p className="font-semibold">Unable to edit form</p>
          <p className="mt-1">
            Your doctor has already reviewed your data. It is no longer possible to make changes.
          </p>
        </div>
      )}

      <form
        id="edit-food-log-form"
        className="flex flex-col gap-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor="context">Meal</Label>
          <Controller
            name="context"
            control={control}
            render={({ field }) => (
              <SelectInput
                id="context"
                disabled={isPending || !form.editable}
                value={field.value}
                options={mealContextOptions}
                onChange={(e) => field.onChange(e.target.value as MealContext)}
              />
            )}
          />
          {errors.context && <p className="text-sm text-error">{errors.context.message}</p>}
        </div>

        <Controller
          name="dateTime"
          control={control}
          render={({ field }) => (
            <DateTimePicker
              value={field.value}
              onChange={field.onChange}
              disabled={isPending || !form.editable}
            />
          )}
        />

        <div className="flex flex-col gap-1">
          <Label htmlFor="mealContents">What did you eat?</Label>
          <TextareaInput
            id="mealContents"
            rows={3}
            placeholder="e.g. salad, grilled chicken…"
            disabled={isPending || !form.editable}
            isError={!!errors.mealContents}
            {...register("mealContents")}
          />
          {errors.mealContents && (
            <p className="text-sm text-error">{errors.mealContents.message}</p>
          )}
        </div>

        <div className="flex gap-4">
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="carbsInGrams">Carbs (g, optional)</Label>
            <TextInput
              id="carbsInGrams"
              type="number"
              min={0}
              max={1000}
              step={1}
              disabled={isPending || !form.editable}
              isError={!!errors.carbsInGrams}
              {...register("carbsInGrams", { valueAsNumber: true })}
            />
            {errors.carbsInGrams && (
              <p className="text-sm text-error">{errors.carbsInGrams.message}</p>
            )}
          </div>

          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="proteinInGrams">Protein (g, optional)</Label>
            <TextInput
              id="proteinInGrams"
              type="number"
              min={0}
              max={1000}
              step={1}
              disabled={isPending || !form.editable}
              isError={!!errors.proteinInGrams}
              {...register("proteinInGrams", { valueAsNumber: true })}
            />
            {errors.proteinInGrams && (
              <p className="text-sm text-error">{errors.proteinInGrams.message}</p>
            )}
          </div>
        </div>

        <NotesField
          disabled={isPending || !form.editable}
          isError={!!errors.notes}
          {...register("notes")}
        />
        {errors.notes && <p className="-mt-4 text-sm text-error">{errors.notes.message}</p>}
      </form>

      <Button
        type="submit"
        form="edit-food-log-form"
        disabled={isPending || !form.editable}
        className="flex w-full gap-3 py-2 text-base"
      >
        {isPending && <Spinner />}
        Save meal log
      </Button>
    </div>
  )
}

const EditFoodLogPage = () => {
  const { formId } = useParams<{ formId: string }>()
  const location = useLocation()
  const state = location.state as { initialTab?: string } | null
  const returnUrl = `${ROUTES.DASHBOARD}?tab=${state?.initialTab ?? "meal-logs"}`
  const { data: self } = useSelfQuery()
  const patientId = self?.id ?? ""

  const { data: form, isLoading } = useFormByIdQuery({ formId: formId ?? null })

  if (isLoading) {
    return (
      <Page showAppbar={false}>
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      </Page>
    )
  }

  if (!form) {
    return (
      <Page showAppbar={false}>
        <div className="px-4 py-8">
          <p className="text-primary-contrast/70">Food log not found.</p>
        </div>
      </Page>
    )
  }

  return (
    <Page showAppbar={false}>
      <FoodLogEditForm form={form} patientId={patientId} returnUrl={returnUrl} />
    </Page>
  )
}

export default EditFoodLogPage
